import { hasDefinedValue } from '@sgme/fp';
import { UnderlyingTag } from 'components/underlying-tag';
import { useUnderlyingContext } from 'hooks/useUnderlyingContext';
import { StrikeMetadata } from 'models/forms/fields';
import type { CommodityUnderlying } from 'models/underlying/commodity';
import { FC } from 'react';
import { getUnderlyingName } from 'utils/underlying/underlying-code';

import { ActionCell, CodeCell, NameCell, StrikeCell, WeightCell } from '../../Common/Cells';
import { Row } from '../../Common/Row';
import { TableWithHiddenColumn } from '../../Common/Table';
import { useCodeTypeSwitcher } from '../../Table.hooks';

export interface CommodityTableProps {
  underlyings: CommodityUnderlying[];
  strikeMetadata: StrikeMetadata;
}

export const CommodityTable: FC<CommodityTableProps> = ({ underlyings, strikeMetadata }) => {
  const [currentCodeType, switchCurrentCodeType] = useCodeTypeSwitcher(underlyings);
  const { canBeRemoved, isReadOnly } = useUnderlyingContext();
  const showWeight = underlyings.some(hasDefinedValue('weight'));

  return (
    <TableWithHiddenColumn columnName="name">
      <thead>
        <tr>
          <CodeCell.Header type={currentCodeType} switchCurrentCodeType={switchCurrentCodeType} />
          <NameCell.Header />
          <WeightCell.Header showWeight={showWeight} />
          <StrikeCell.Header strikeMetadata={strikeMetadata} />
          <ActionCell.RemoveAll />
        </tr>
      </thead>
      <tbody>
        {underlyings.map(underlying => (
          <Row key={underlying.id}>
            <CodeCell.Data>
              <UnderlyingTag
                value={underlying}
                label={getUnderlyingName(underlying, currentCodeType)}
                isReadOnly
                size="lg"
              />
            </CodeCell.Data>
            <NameCell.Data underlying={underlying}>{underlying.name}</NameCell.Data>
            <WeightCell.Data
              underlyingId={underlying.id}
              weightValue={underlying.weight}
              showWeight={showWeight}
              isWeightInPercent={underlying.isWeightInPercent}
            ></WeightCell.Data>
            {underlying.strike && (
              <StrikeCell.Data
                strikeMetadata={strikeMetadata}
                {...underlying.strike}
                underlyingId={underlying.id}
                currency={underlying.currency}
              />
            )}
            {Boolean(canBeRemoved) && !isReadOnly && <ActionCell.Remove underlyingId={underlying.id} />}
          </Row>
        ))}
      </tbody>
    </TableWithHiddenColumn>
  );
};

CommodityTable.displayName = 'CommodityTable';
